/**
 * Pieces shared by the CSV, XLSX and PDF exporters, so a screen declares its
 * columns and file naming once and every button stays in step.
 */

/**
 * What a single exported cell may hold.
 *
 * `Date` is deliberately part of this rather than every screen pre-formatting
 * its timestamps to strings. Only the XLSX exporter can act on a real date —
 * it writes a genuine date cell carrying a display format, which is what stops
 * Excel from rendering `###`. CSV and PDF are text formats with no such
 * concept, so they flatten it with `formatReportDate` below.
 */
export type ReportValue = string | number | boolean | Date | null | undefined;

/**
 * One column of an exported report. `value` pulls a single cell out of a row,
 * flattening whatever nesting the model has.
 */
export interface ReportColumn<T> {
    header: string,
    value: (row: T) => ReportValue,
    /** Sum this column into a totals row/footer appended by the exporters. */
    total?: boolean
}

/**
 * The one date rendering shared by every export, matching the `toDateTime`
 * filter the tables render with, so a report reads the same as the screen it
 * came from.
 */
export function formatReportDate(value: Date): string {
    const pad = (n: number) => String(n).padStart(2, '0');
    return `${value.getFullYear()}-${pad(value.getMonth() + 1)}-${pad(value.getDate())}` +
        ` ${pad(value.getHours())}:${pad(value.getMinutes())}`;
}

/**
 * Flattens any cell to the text the CSV and PDF exporters need.
 */
export function toReportText(value: ReportValue): string {
    if (value === null || value === undefined) {
        return '';
    }
    return value instanceof Date ? formatReportDate(value) : String(value);
}

/**
 * Sums the `total: true` columns across every row. Non-total columns come
 * back `undefined` so exporters can render a blank cell instead of a 0 that
 * would misread as a real total for, say, a name column.
 */
export function reportTotals<T>(rows: T[], columns: ReportColumn<T>[]): (number | undefined)[] {
    return columns.map((column) => {
        if (!column.total) {
            return undefined;
        }
        return rows.reduce((sum, row) => {
            const value = column.value(row);
            return sum + (typeof value === 'number' ? value : 0);
        }, 0);
    });
}

/**
 * Builds a file name of the shape `rides_Acme-Taxis_2026-08-01-2026-08-21.csv`.
 * Anything that upsets a file system is replaced, so company names containing
 * slashes or colons cannot produce an unsaveable download.
 */
export function reportFileName(
    prefix: string,
    scope: string | null,
    from: string | null,
    to: string | null,
    extension: string
): string {
    const parts = [prefix];
    if (scope) {
        parts.push(scope.replace(/[^\w\-.]+/g, '-'));
    }
    if (from && to) {
        parts.push(`${from}-${to}`);
    }
    return `${parts.join('_')}.${extension}`;
}
